import React, { useCallback, useMemo } from 'react';
import { type Href, useRouter } from 'expo-router';
import { FormattedMessage } from 'react-intl';

import { Button, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { giftCardRedemptionCtaMessages as messages } from './GiftCardRedemptionCta.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardRedemptionCta = (props: GiftCardRedemptionCtaProps) => {
  const { redeemId } = props;

  const router = useRouter();
  const { currentBreakpoint } = useResponsive();
  const isLoggedIn = useIsLoggedIn();
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore

  // ─── Derived ─────────────────────────────────────────────────────────

  const redirectTo = useMemo<Href>(() => {
    const encodedRedeemId = encodeURIComponent(redeemId);

    if (currentBreakpoint.isXS) {
      return `/redeem-gift-card/${encodedRedeemId}`;
    }

    if (isInStoreOrdersEnabled) {
      return `/account/gift-cards/${encodedRedeemId}`;
    }

    return `/add-gift-card/${encodedRedeemId}`;
  }, [currentBreakpoint.isXS, isInStoreOrdersEnabled, redeemId]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleAddToAccountPress = useCallback(() => {
    if (!isLoggedIn) {
      router.navigate({
        pathname: '/(auth)/join-or-sign-in',
        params: { redirect: redirectTo as string },
      });

      return;
    }

    router.navigate(redirectTo);
  }, [isLoggedIn, redirectTo, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Button size="large" onPress={handleAddToAccountPress}>
      <FormattedMessage {...messages.label} />
    </Button>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardRedemptionCtaProps = {
  redeemId: string;
};
